import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../utils/axiosConfig';
import {
  IntegrationAlert,
  IntegrationIncident,
  IntegrationDevice,
  IntegrationSummary,
  IntegrationDataState,
  IntegrationAlertFilters,
  IntegrationIncidentFilters,
  IntegrationDeviceFilters,
  IntegrationAlertsResponse,
  IntegrationIncidentsResponse,
  IntegrationDevicesResponse,
} from '../../types';

// Initial state
const initialState: IntegrationDataState = {
  alerts: [],
  selectedAlert: null,
  alertsPagination: {
    total: 0,
    limit: 100,
    offset: 0,
  },

  incidents: [],
  selectedIncident: null,
  incidentsPagination: {
    total: 0,
    limit: 100,
    offset: 0,
  },

  devices: [],
  selectedDevice: null,
  devicesPagination: {
    total: 0,
    limit: 100,
    offset: 0,
  },

  summary: null,

  isLoading: false,
  error: null,
};

// Helper function to build query params
const buildQueryParams = (filters: Record<string, any>): string => {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ==================== ALERTS ====================

export const fetchIntegrationAlerts = createAsyncThunk(
  'integrationData/fetchIntegrationAlerts',
  async (filters: IntegrationAlertFilters = {}, { rejectWithValue }) => {
    try {
      const queryString = buildQueryParams(filters);
      const response = await api.get<IntegrationAlertsResponse>(
        `/integration-data/integration-alerts${queryString}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch integration alerts');
    }
  }
);

export const fetchIntegrationAlertById = createAsyncThunk(
  'integrationData/fetchIntegrationAlertById',
  async (alertId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<IntegrationAlert>(
        `/integration-data/integration-alerts/${alertId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch integration alert');
    }
  }
);

// ==================== INCIDENTS ====================

export const fetchIntegrationIncidents = createAsyncThunk(
  'integrationData/fetchIntegrationIncidents',
  async (filters: IntegrationIncidentFilters = {}, { rejectWithValue }) => {
    try {
      const queryString = buildQueryParams(filters);
      const response = await api.get<IntegrationIncidentsResponse>(
        `/integration-data/integration-incidents${queryString}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch integration incidents');
    }
  }
);

export const fetchIntegrationIncidentById = createAsyncThunk(
  'integrationData/fetchIntegrationIncidentById',
  async (incidentId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<IntegrationIncident>(
        `/integration-data/integration-incidents/${incidentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch integration incident');
    }
  }
);

// ==================== DEVICES ====================

export const fetchIntegrationDevices = createAsyncThunk(
  'integrationData/fetchIntegrationDevices',
  async (filters: IntegrationDeviceFilters = {}, { rejectWithValue }) => {
    try {
      const queryString = buildQueryParams(filters);
      const response = await api.get<IntegrationDevicesResponse>(
        `/integration-data/integration-devices${queryString}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch integration devices');
    }
  }
);

export const fetchIntegrationDeviceById = createAsyncThunk(
  'integrationData/fetchIntegrationDeviceById',
  async (deviceId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<IntegrationDevice>(
        `/integration-data/integration-devices/${deviceId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch integration device');
    }
  }
);

// ==================== SUMMARY ====================

export const fetchIntegrationSummary = createAsyncThunk(
  'integrationData/fetchIntegrationSummary',
  async (filters: { client_id?: string; integration_id?: string } = {}, { rejectWithValue }) => {
    try {
      const queryString = buildQueryParams(filters);
      const response = await api.get<IntegrationSummary>(
        `/integration-data/integration-summary${queryString}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch integration summary');
    }
  }
);

// ==================== SLICE ====================

const integrationDataSlice = createSlice({
  name: 'integrationData',
  initialState,
  reducers: {
    clearSelectedAlert: (state) => {
      state.selectedAlert = null;
    },
    clearSelectedIncident: (state) => {
      state.selectedIncident = null;
    },
    clearSelectedDevice: (state) => {
      state.selectedDevice = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAllData: (state) => {
      state.alerts = [];
      state.incidents = [];
      state.devices = [];
      state.summary = null;
      state.selectedAlert = null;
      state.selectedIncident = null;
      state.selectedDevice = null;
    },
  },
  extraReducers: (builder) => {
    // ==================== ALERTS ====================

    // Fetch integration alerts
    builder.addCase(fetchIntegrationAlerts.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchIntegrationAlerts.fulfilled, (state, action) => {
      state.isLoading = false;
      state.alerts = action.payload.alerts;
      state.alertsPagination = {
        total: action.payload.total,
        limit: action.payload.limit,
        offset: action.payload.offset,
      };
    });
    builder.addCase(fetchIntegrationAlerts.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch integration alert by ID
    builder.addCase(fetchIntegrationAlertById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchIntegrationAlertById.fulfilled, (state, action) => {
      state.isLoading = false;
      state.selectedAlert = action.payload;
    });
    builder.addCase(fetchIntegrationAlertById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // ==================== INCIDENTS ====================

    // Fetch integration incidents
    builder.addCase(fetchIntegrationIncidents.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchIntegrationIncidents.fulfilled, (state, action) => {
      state.isLoading = false;
      state.incidents = action.payload.incidents;
      state.incidentsPagination = {
        total: action.payload.total,
        limit: action.payload.limit,
        offset: action.payload.offset,
      };
    });
    builder.addCase(fetchIntegrationIncidents.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch integration incident by ID
    builder.addCase(fetchIntegrationIncidentById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchIntegrationIncidentById.fulfilled, (state, action) => {
      state.isLoading = false;
      state.selectedIncident = action.payload;
    });
    builder.addCase(fetchIntegrationIncidentById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // ==================== DEVICES ====================

    // Fetch integration devices
    builder.addCase(fetchIntegrationDevices.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchIntegrationDevices.fulfilled, (state, action) => {
      state.isLoading = false;
      state.devices = action.payload.devices;
      state.devicesPagination = {
        total: action.payload.total,
        limit: action.payload.limit,
        offset: action.payload.offset,
      };
    });
    builder.addCase(fetchIntegrationDevices.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch integration device by ID
    builder.addCase(fetchIntegrationDeviceById.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchIntegrationDeviceById.fulfilled, (state, action) => {
      state.isLoading = false;
      state.selectedDevice = action.payload;
    });
    builder.addCase(fetchIntegrationDeviceById.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // ==================== SUMMARY ====================

    // Fetch integration summary
    builder.addCase(fetchIntegrationSummary.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchIntegrationSummary.fulfilled, (state, action) => {
      state.isLoading = false;
      state.summary = action.payload;
    });
    builder.addCase(fetchIntegrationSummary.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const {
  clearSelectedAlert,
  clearSelectedIncident,
  clearSelectedDevice,
  clearError,
  clearAllData,
} = integrationDataSlice.actions;

export default integrationDataSlice.reducer;
